import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';

const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARN_BEFORE_MS = 2 * 60 * 1000;

export default function IdleTimeout() {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const id = setInterval(async () => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= IDLE_LIMIT_MS) {
        clearInterval(id);
        sessionStorage.setItem('signedOutReason', 'inactivity');
        try {
          await signOut(auth);
        } finally {
          navigate('/login', { replace: true });
        }
        return;
      }

      if (idle >= IDLE_LIMIT_MS - WARN_BEFORE_MS) {
        setSecondsLeft(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
      } else {
        setSecondsLeft((prev) => (prev === null ? prev : null));
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, markActive));
      clearInterval(id);
    };
  }, [navigate]);

  if (secondsLeft === null) return null;

  const stay = () => {
    lastActivityRef.current = Date.now();
    setSecondsLeft(null);
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-warn-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: '#111113',
          border: '1px solid #2A2A2C',
          borderRadius: 14,
          padding: '28px 32px',
          maxWidth: 380,
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.65)',
        }}
      >
        <div
          style={{
            color: '#E63946',
            fontSize: 11,
            letterSpacing: 2,
            fontWeight: 800,
            marginBottom: 10,
          }}
        >
          INACTIVITY WARNING
        </div>
        <h3
          id="idle-warn-title"
          style={{
            color: '#F7E8BF',
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: -0.2,
          }}
        >
          You'll be signed out in {mm}:{ss}
        </h3>
        <p
          style={{
            color: '#9A9A9A',
            fontSize: 13,
            lineHeight: 1.55,
            margin: '10px 0 22px',
          }}
        >
          For security, your session ends if no activity is detected.
          Click below to stay signed in.
        </p>
        <button
          onClick={stay}
          autoFocus
          style={{
            background: '#D4B574',
            color: '#000',
            border: 'none',
            padding: '11px 24px',
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Stay signed in
        </button>
      </div>
    </div>
  );
}
